// SubmissionsPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { PageHeader } from "../components/ui/page-header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Download,
  Search,
  Clock,
  CheckCircle,
  FileText,
  BookOpen,
  Code,
  TrendingUp,
  Eye,
  Loader2,
} from "lucide-react";
import { useAuth, useUtilities } from "../App";
import { ROLES } from "../constant/roles";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";

// API
import {
  getAllSubmissions,
  getSubmissionById,
  type SubmissionEntity,
  type GetAllSubmissionsResponse,
} from "../api/submissionApi";
import paths from "../routes";
import FeedbackDialog from "../components/FeedbackDialog";
import {
  GradeResponse,
  type GradeSubmissionRequest,
  saveRubrics,
} from "../api/createGradeApi";
import { useQueryClient } from "@tanstack/react-query";
import { downloadAsXlsx } from "../helper/helperFunctions";

// ----- Types -----
interface RubricItem {
  id: string;
  criteria: string;
  description: string;
  points: number;
  earnedPoints?: number;
}

interface Submission {
  id: string;
  studentName: string;
  studentEmail: string;
  studentId: string;
  submittedAt: string | null;
  submissionId?: string;
  status:
    | "submitted"
    | "completed"
    | "reviewed"
    | "returned"
    | "graded"
    | "ai_grading"
    | "ai_grading_failed"
    | "late";
  grade?: {
    id: string;
    totalScore: string;
    maxScore: string;
    percentage?: string;
    letterGrade?: string;
  } | null;
  feedback?: string;
  gradedAt?: string;
  assignmentId: string;
  attempts?: string;
  assignmentTitle: string;
  checkpointTitle: string;
  course: string;
  courseCode?: string;
  files: {
    name: string;
    size: string | null;
    type: string;
    url?: string;
  }[];
  notebookUrl?: string;
  comments?: string;
  gradeBreakdown?: {
    criteria: string;
    points: number;
    maxPoints: number;
    feedback?: string;
  }[];
  rubric?: RubricItem[];
  group?: {
    id: string;
    name: string;
    code: string;
    members: {
      id: string;
      name: string;
      email: string;
    }[];
  };
}

// ----- Utils -----
function formatBytes(bytes?: number | null): string {
  if (!bytes || bytes < 1) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const val = (bytes / Math.pow(k, i)).toFixed(1);
  return `${val} ${sizes[i]}`;
}

function mapApiStatusToUi(status?: string): Submission["status"] {
  if (!status) return "submitted";
  switch (status) {
    case "completed":
      return "completed";
    case "submitted":
      return "submitted";
    case "reviewed":
      return "reviewed";
    case "returned":
      return "returned";
    case "ai_grading":
      return "ai_grading";
    case "ai_grading_failed":
      return "ai_grading_failed";
    default:
      return "submitted";
  }
}

function mapApiItemToSubmission(apiItem: SubmissionEntity): Submission {
  const {
    user,
    project,
    checkpoint,
    id,
    status,
    submittedAt,
    attempts,
    submissionSummary,
    grade,
    metadata,
  } = apiItem;
  const submissionId = (apiItem as any).submissionId;

  const studentName = user?.name ?? "Unknown Student";
  const studentEmail = user?.email ?? "";
  const studentId = user?.id ?? "";
  const assignmentTitle = project?.title ?? "Untitled Assignment";
  const course = (project as any)?.course?.name ?? "No Course";
  const courseCode = (project as any)?.course?.code ?? "";
  const checkpointTitle = checkpoint?.title ?? "No Checkpoint";

  const metaFiles = Array.isArray((metadata as any)?.files)
    ? (metadata as any).files
    : [];
  const metaNotebooks = Array.isArray((metadata as any)?.notebooks)
    ? (metadata as any).notebooks
    : [];
  const group = apiItem.group || null;

  const toUiFile = (f: any): Submission["files"][number] => {
    const key = f?.key || f?.path || f?.name || f?.url;
    const nameFromKey =
      typeof key === "string" ? key.split("/").pop() || "file" : "file";
    const name = f?.name || nameFromKey;
    const ext = name.split(".").pop()?.toLowerCase();
    const type =
      ext === "ipynb"
        ? "notebook"
        : ext === "pdf"
        ? "document"
        : ext === "ppt" || ext === "pptx"
        ? "presentation"
        : "file";
    const sizeBytes =
      typeof f?.size === "number"
        ? f.size
        : typeof f?.contentLength === "number"
        ? f.contentLength
        : undefined;
    return {
      name,
      size: formatBytes(sizeBytes ?? null),
      type,
      url: f?.url || f?.signedUrl || undefined,
    };
  };

  const notebookFiles: Submission["files"] = metaNotebooks.map((n: any) => {
    const key = n?.key || n?.path || n?.url || n?.name;
    const nameFromKey =
      typeof key === "string"
        ? key.split("/").pop() || "notebook.ipynb"
        : "notebook.ipynb";
    return {
      name: n?.name || nameFromKey,
      size: formatBytes(typeof n?.size === "number" ? n.size : null),
      type: "notebook",
      url: n?.url || n?.signedUrl || undefined,
    };
  });

  const allFiles = [...metaFiles.map(toUiFile), ...notebookFiles];

  const result: Submission = {
    id,
    studentName,
    studentEmail,
    studentId,
    submittedAt: submittedAt ?? null,
    submissionId,
    status: mapApiStatusToUi(status),
    assignmentId: project?.id ?? "",
    assignmentTitle,
    checkpointTitle,
    course,
    courseCode,
    attempts: String(attempts ?? ""),
    grade: (grade as any) ?? null,
    files: allFiles,
  };

  if (submissionSummary) {
    result.comments = submissionSummary;
  }
  if (notebookFiles[0]?.url) {
    result.notebookUrl = notebookFiles[0].url;
  }
  if (group) {
    result.group = group;
  }

  return result;
}

// ----- Student view (unchanged) -----
function StudentSubmissionsView({
  submissions,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  courseFilter,
  setCourseFilter,
  selectedSubmission,
  setSelectedSubmission,
  onFetchDetail,
  isFetchingDetail,
}: {
  submissions: Submission[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  courseFilter: string;
  setCourseFilter: (course: string) => void;
  selectedSubmission: Submission | null;
  setSelectedSubmission: (submission: Submission | null) => void;
  onFetchDetail: (submissionId: string) => void;
  isFetchingDetail: boolean;
}) {
  const navigate = useNavigate();
  const { downloadSubmission } = useUtilities();

  const filteredSubmissions = useMemo(() => {
    return submissions.filter((submission) => {
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch =
        term.length === 0 ||
        submission.assignmentTitle.toLowerCase().includes(term) ||
        submission.course.toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === "all" || submission.status === statusFilter;
      const matchesCourse =
        courseFilter === "all" || submission.course === courseFilter;
      return matchesSearch && matchesStatus && matchesCourse;
    });
  }, [submissions, searchTerm, statusFilter, courseFilter]);

  const submissionStats = useMemo(
    () => ({
      total: submissions.length,
      submitted: submissions.filter((s) => s.status === "submitted").length,
      graded: submissions.filter((s) => s.status === "graded").length,
      late: submissions.filter((s) => s.status === "late").length,
    }),
    [submissions]
  );

  const courses = useMemo(
    () => Array.from(new Set(submissions.map((s) => s.course))),
    [submissions]
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case "submitted":
        return "bg-blue-100 text-blue-800";
      case "reviewed":
        return "bg-purple-100 text-purple-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "returned":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  const handleViewNotebook = (submission: Submission) => {
    navigate("notebook", {
      state: {
        projectId: submission.assignmentId,
        projectTitle: submission.assignmentTitle,
        context: "submission-review",
        submissionId: submission.id,
      },
    });
  };

  const handleDownloadFiles = (submission: Submission) => {
    downloadSubmission("downloadSubmission", {
      id: submission.id,
      assignmentTitle: submission.assignmentTitle,
      studentName: submission.studentName,
      submissionDate: submission.submittedAt,
      status: submission.status,
      grade: submission.grade,
      feedback: submission.feedback,
      files: submission.files.map((f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
        url: f.url,
      })),
      notebooks: submission.notebookUrl ? [submission.notebookUrl] : [],
      datasets: [],
    });
  };

  const calculateGPA = () => {
    const gradedSubmissions = submissions.filter(
      (s) => s.rubric && s.status === "graded"
    );
    if (gradedSubmissions.length === 0) return "0.00";
    let totalPoints = 0;
    let maxTotalPoints = 0;
    gradedSubmissions.forEach((submission) => {
      if (submission.rubric) {
        submission.rubric.forEach((item) => {
          totalPoints += item.earnedPoints ?? 0;
          maxTotalPoints += item.points;
        });
      }
    });
    const gpa = maxTotalPoints === 0 ? 0 : (totalPoints / maxTotalPoints) * 4.0;
    return gpa.toFixed(2);
  };

  return (
    // <div className="space-y-4">
    //   {/* Stats */}
    //   <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
    //     <Card>
    //       <CardContent className="p-4">
    //         <div className="flex items-center justify-between">
    //           <div>
    //             <div className="text-2xl font-bold text-bits-blue">
    //               {submissionStats.total}
    //             </div>
    //             <p className="text-sm text-muted-foreground">
    //               Total Submissions
    //             </p>
    //           </div>
    //           <FileText className="h-8 w-8 text-bits-blue" />
    //         </div>
    //       </CardContent>
    //     </Card>
    //     <Card>
    //       <CardContent className="p-4">
    //         <div className="flex items-center justify-between">
    //           <div>
    //             <div className="text-2xl font-bold text-blue-600">
    //               {submissionStats.submitted}
    //             </div>
    //             <p className="text-sm text-muted-foreground">Awaiting Grade</p>
    //           </div>
    //           <Clock className="h-8 w-8 text-blue-600" />
    //         </div>
    //       </CardContent>
    //     </Card>
    //     <Card>
    //       <CardContent className="p-4">
    //         <div className="flex items-center justify-between">
    //           <div>
    //             <div className="text-2xl font-bold text-green-600">
    //               {submissionStats.graded}
    //             </div>
    //             <p className="text-sm text-muted-foreground">Graded</p>
    //           </div>
    //           <CheckCircle className="h-8 w-8 text-green-600" />
    //         </div>
    //       </CardContent>
    //     </Card>
    //     <Card>
    //       <CardContent className="p-4">
    //         <div className="flex items-center justify-between">
    //           <div>
    //             <div className="text-2xl font-bold text-orange-600">
    //               {calculateGPA()}
    //             </div>
    //             <p className="text-sm text-muted-foreground">Current GPA</p>
    //           </div>
    //           <TrendingUp className="h-8 w-8 text-orange-600" />
    //         </div>
    //       </CardContent>
    //     </Card>
    //   </div>

    //   {/* List */}
    //   <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    //     <div className="lg:col-span-2">
    //       <Card>
    //         <CardHeader>
    //           <div className="flex flex-wrap items-center justify-between">
    //             <CardTitle>My Submissions</CardTitle>
    //             <div className="flex space-x-2">
    //               <div className="relative">
    //                 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
    //                 <Input
    //                   placeholder="Search assignments..."
    //                   value={searchTerm}
    //                   onChange={(e) => setSearchTerm(e.target.value)}
    //                   className="pl-9 w-64"
    //                 />
    //               </div>

    //               <Select value={courseFilter} onValueChange={setCourseFilter}>
    //                 <SelectTrigger className="w-40">
    //                   <SelectValue placeholder="All Courses" />
    //                 </SelectTrigger>
    //                 <SelectContent>
    //                   <SelectItem value="all">All Courses</SelectItem>
    //                   {Array.from(
    //                     new Set(submissions.map((s) => s.course))
    //                   ).map((course) => (
    //                     <SelectItem key={course} value={course}>
    //                       {course}
    //                     </SelectItem>
    //                   ))}
    //                 </SelectContent>
    //               </Select>

    //               <Select value={statusFilter} onValueChange={setStatusFilter}>
    //                 <SelectTrigger className="w-32">
    //                   <SelectValue placeholder="All Status" />
    //                 </SelectTrigger>
    //                 <SelectContent>
    //                   <SelectItem value="all">All Status</SelectItem>
    //                   <SelectItem value="submitted">Submitted</SelectItem>
    //                   <SelectItem value="graded">Graded</SelectItem>
    //                   <SelectItem value="late">Late</SelectItem>
    //                   <SelectItem value="inprogress">In Progress</SelectItem>
    //                 </SelectContent>
    //               </Select>
    //             </div>
    //           </div>
    //         </CardHeader>
    //         <CardContent>
    //           <div className="space-y-4">
    //             {filteredSubmissions.map((submission) => (
    //               <div
    //                 key={submission.id}
    //                 className="border rounded-lg p-4 transition-colors hover:bg-gray-50"
    //               >
    //                 <div className="flex items-center justify-between mb-3">
    //                   <div className="flex items-center space-x-3">
    //                     <div className="w-10 h-10 bg-bits-blue rounded-full flex items-center justify-center">
    //                       <BookOpen className="h-5 w-5 text-white" />
    //                     </div>
    //                     <div>
    //                       <p className="font-medium">
    //                         {submission.assignmentTitle}
    //                       </p>
    //                       <p className="text-sm text-muted-foreground">
    //                         {submission.course}
    //                       </p>
    //                     </div>
    //                   </div>
    //                   <div className="flex items-center space-x-2">
    //                     <Badge className={getStatusColor(submission.status)}>
    //                       {submission.status.charAt(0).toUpperCase() +
    //                         submission.status.slice(1)}
    //                     </Badge>
    //                     {submission.grade && (
    //                       <Badge variant="outline" className="font-bold">
    //                         {typeof submission.grade === "object"
    //                           ? submission.grade.letterGrade ??
    //                             `${submission.grade.totalScore}/${submission.grade.maxScore}`
    //                           : submission.grade}
    //                         {(submission as any).grade?.percentage != null
    //                           ? ` ${(submission as any).grade?.percentage}`
    //                           : ""}
    //                       </Badge>
    //                     )}
    //                   </div>
    //                 </div>

    //                 <div className="flex items-center justify-between text-sm">
    //                   <div className="flex items-center space-x-4">
    //                     <div className="flex items-center space-x-1">
    //                       <Clock className="h-4 w-4 text-muted-foreground" />
    //                       <span>
    //                         {submission.submittedAt
    //                           ? new Date(
    //                               submission.submittedAt
    //                             ).toLocaleDateString("en-GB")
    //                           : "-"}
    //                       </span>
    //                     </div>
    //                     <div className="flex items-center space-x-1">
    //                       <FileText className="h-4 w-4 text-muted-foreground" />
    //                       <span>{submission.files.length} files</span>
    //                     </div>
    //                   </div>
    //                   <div className="flex space-x-2">
    //                     <Button
    //                       size="sm"
    //                       variant="outline"
    //                       onClick={() => handleDownloadFiles(submission)}
    //                     >
    //                       <Download className="h-4 w-4 mr-2" /> Download
    //                     </Button>
    //                     {submission.notebookUrl && (
    //                       <Button
    //                         size="sm"
    //                         variant="outline"
    //                         onClick={() => handleViewNotebook(submission)}
    //                         className="bg-bits-blue text-white hover:bg-bits-blue/90"
    //                       >
    //                         <Code className="h-4 w-4 mr-2" /> View Work
    //                       </Button>
    //                     )}
    //                   </div>
    //                 </div>
    //               </div>
    //             ))}

    //             {filteredSubmissions.length === 0 && (
    //               <div className="text-center py-8">
    //                 <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
    //                 <p className="text-muted-foreground">
    //                   No submissions found matching your criteria
    //                 </p>
    //               </div>
    //             )}
    //           </div>
    //         </CardContent>
    //       </Card>
    //     </div>

    //     <div className="hidden lg:block">{/* details panel placeholder */}</div>
    //   </div>
    // </div>
    <></>
  );
}

// ----- Instructor view (with ProjectManagement-style pagination) -----
function InstructorSubmissionsView({
  serverRows,
  loading,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  projectId,
  setProjectId,
  courseId,
  setCourseId,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  page,
  setPage,
  limit,
  setLimit,
  totalPages,
  totalItems,
  filterProjects,
  filterCourses,
  onGradeSubmit,
  onRefetchTrigger, // Add this prop
}: {
  serverRows: Submission[];
  loading: boolean;

  searchTerm: string;
  setSearchTerm: (v: string) => void;

  statusFilter: string;
  setStatusFilter: (v: string) => void;

  projectId: string;
  setProjectId: (v: string) => void;

  courseId: string;
  setCourseId: (v: string) => void;

  fromDate: string;
  setFromDate: (v: string) => void;

  toDate: string;
  setToDate: (v: string) => void;

  sortBy: string;
  setSortBy: (v: string) => void;

  sortOrder: "asc" | "desc";
  setSortOrder: (v: "asc" | "desc") => void;

  page: number;
  setPage: (n: number) => void;

  limit: number;
  setLimit: (n: number) => void;

  totalPages: number;
  totalItems: number;

  filterProjects: { id: string; title: string }[];
  filterCourses: { id: string; title: string }[];

  onGradeSubmit: (payload: GradeSubmissionRequest) => Promise<void>;
  onRefetchTrigger: () => void; // Add type
}) {
  const navigate = useNavigate();
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [currentSubmission, setCurrentSubmission] = useState<Submission | null>(
    null
  );
  const [gradingSubmissionId, setGradingSubmissionId] = useState<string | null>(
    null
  );

  const queryClient = useQueryClient();

  const handleFeedbackSubmit = async (
    payloadOrBody: GradeResponse | GradeSubmissionRequest | any,
    opts?: { source?: string }
  ) => {
    try {
      if (opts?.source === "dialog") {
        const serverPayload =
          payloadOrBody?.data && payloadOrBody.data.data
            ? payloadOrBody.data
            : payloadOrBody;

        const gradeResp: GradeResponse = (serverPayload?.data ??
          serverPayload) as GradeResponse;

        if (currentSubmission?.id) {
          queryClient.invalidateQueries({
            queryKey: ["progressDetails", currentSubmission.id],
          });
        }

        onRefetchTrigger();

        // Toast already shown by FeedbackDialog, no need to show again
        return;
      }
      const response: GradeResponse = await saveRubrics(
        payloadOrBody as GradeSubmissionRequest
      );

      toast.success(
        (response as any).message || "Feedback submitted successfully!"
      );

      if (currentSubmission?.id) {
        queryClient.invalidateQueries({
          queryKey: ["progressDetails", currentSubmission.id],
        });
      }

      // Trigger refetch of submissions list
      onRefetchTrigger();
    } catch (error: any) {
      console.error("Error saving feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    }
  };

  const statusBadgeClass = (status: Submission["status"]) => {
    switch (status) {
      case "submitted":
        return "bg-blue-100 text-blue-800";
      case "reviewed":
        return "bg-purple-100 text-purple-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "returned":
        return "bg-amber-100 text-amber-800";
      case "ai_grading":
        return "bg-purple-100 text-purple-700";
      case "ai_grading_failed":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="rounded-lg border p-3 bg-white flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search students, checkpoint, project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        {(() => {
          const [dateOpen, setDateOpen] = React.useState(false);
          const currentMode: "newest" | "oldest" | "custom" =
            fromDate || toDate
              ? "custom"
              : sortOrder === "asc"
              ? "oldest"
              : "newest";

          const applyPreset = (mode: "newest" | "oldest") => {
            setFromDate("");
            setToDate("");
            setSortOrder(mode === "newest" ? "desc" : "asc");
            setDateOpen(false);
          };

          const displayLabel =
            currentMode === "custom"
              ? `Custom: ${fromDate || "—"} → ${toDate || "—"}`
              : currentMode === "newest"
              ? "Newest"
              : "Oldest";

          return (
            <div className="relative">
              <button
                type="button"
                className="inline-flex items-center justify-between rounded-md border px-3 py-2 text-sm w-44 bg-white"
                onClick={() => setDateOpen((o) => !o)}
              >
                <span className="truncate">Date: {displayLabel}</span>
                <svg
                  className="ml-2 h-4 w-4 opacity-60"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 011.08 1.04l-4.24 4.64a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" />
                </svg>
              </button>

              {dateOpen && (
                <div className="absolute z-50 mt-2 w-54 rounded-md border bg-white p-2 shadow">
                  <button
                    className={`w-full text-left px-2 py-2 rounded hover:bg-gray-50 ${
                      currentMode === "newest" ? "bg-gray-50" : ""
                    }`}
                    onClick={() => applyPreset("newest")}
                  >
                    Newest
                  </button>

                  <button
                    className={`w-full text-left px-2 py-2 rounded hover:bg-gray-50 ${
                      currentMode === "oldest" ? "bg-gray-50" : ""
                    }`}
                    onClick={() => applyPreset("oldest")}
                  >
                    Oldest
                  </button>

                  <div className="my-2 h-px bg-gray-200" />

                  <div className="px-2 py-1 flex-row">
                    <div className="text-xs font-medium text-gray-500 mb-2">
                      Custom range
                    </div>
                    <div className="flex-row items-center gap-2 space-y-1">
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => {
                          setFromDate(e.target.value);
                          if (e.target.value && toDate) setDateOpen(false);
                        }}
                        className="w-[150px] border rounded px-2 py-1 text-sm"
                      />
                      <span className="flex ml-16 text-sm text-muted-foreground">
                        to
                      </span>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => {
                          setToDate(e.target.value);
                          if (fromDate && e.target.value) setDateOpen(false);
                        }}
                        className="w-[150px] border rounded px-2 py-1 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="w-56 truncate">
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {filterProjects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={courseId} onValueChange={setCourseId}>
          <SelectTrigger className="w-56 truncate">
            <SelectValue placeholder="All Courses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Courses</SelectItem>
            {filterCourses.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="gap-0">
        <CardHeader>
          <CardTitle className="text-base font-semibold">
            All Submissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full mt-2 !p-4">
              <thead>
                <tr className="text-left text-sm text-gray-700 border-b">
                  <th className="py-3 pr-4">Student</th>
                  <th className="py-3 pr-4">Checkpoint / Project</th>
                  <th className="py-3 pr-4">Type</th>
                  <th className="py-3 pr-4">Course</th>
                  <th className="py-3 pr-4 text-center">Date</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Attempts</th>
                  <th className="py-3 pr-4">Score</th>
                  <th className="py-3 pl-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {serverRows.map((s) => (
                  <tr key={s.id} className="border-b">
                    <td className="py-3 pr-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {s.studentName}
                        </span>
                        <span className="text-xs text-muted-foreground pt-2">
                          {s.studentEmail}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {s.checkpointTitle}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {s.assignmentTitle}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 pr-4 ">
                      <span className="text-xs text-muted-foreground">
                        {s.group ? "Group" : "Individual"}
                      </span>
                    </td>
                    <td className="flex flex-col py-3 pr-4 ">
                      <span className="text-sm font-medium">{s.course}</span>
                      <span className="text-xs text-muted-foreground">
                        {s.courseCode}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-sm text-center text-muted-foreground">
                      {s.submittedAt
                        ? new Date(s.submittedAt).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${statusBadgeClass(
                          s.status
                        )}`}
                      >
                        {s.status === "ai_grading" ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            AI Evaluating...
                          </>
                        ) : s.status === "ai_grading_failed" ? (
                          "AI Eval Failed"
                        ) : (
                          s.status.charAt(0).toUpperCase() + s.status.slice(1)
                        )}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-center">{s.attempts}</td>
                    <td className="py-3 pr-4 text-center">
                      {s.grade
                        ? `${(s as any).grade?.totalScore ?? "-"} / ${
                            (s as any).grade?.maxScore ?? "-"
                          }`
                        : "-"}
                    </td>
                    <td className="py-3 pl-2">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="default"
                          className="bg-bits-blue text-white hover:bg-bits-blue/90"
                          onClick={() => {
                            setCurrentSubmission(s);
                            setGradingSubmissionId(s.submissionId || "");
                            setIsFeedbackDialogOpen(true);
                          }}
                        >
                          Grade
                        </Button>
                        <Button
                          size="icon"
                          variant="outline"
                          onClick={() =>
                            navigate(
                              paths.submissionDetails.replace(
                                ":submissionId",
                                s.id!
                              ),
                              {
                                state: {
                                  submissions: serverRows,
                                  currentSubmissionId: s.id,
                                  assignmentTitle: s.assignmentTitle,
                                },
                              }
                            )
                          }
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}

                {serverRows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={8} className="py-8">
                      <div className="text-center text-muted-foreground">
                        <FileText className="h-12 w-12 mx-auto mb-4" />
                        No submissions found matching filters
                      </div>
                    </td>
                  </tr>
                )}
                {loading && (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-6 text-center text-muted-foreground"
                    >
                      Loading...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ProjectManagement-style Pagination */}
          <div className="flex items-center justify-between pt-2">
            <div className="text-sm text-bits-grey-600">
              Page {page} of {Math.max(1, totalPages)} • {totalItems} items
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage(Math.max(1, page - 1))}
              >
                Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= Math.max(1, totalPages) || loading}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>

              <Select
                value={String(limit)}
                onValueChange={(v) => {
                  setLimit(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-24 bg-bits-grey-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 30, 50].map((l) => (
                    <SelectItem key={l} value={String(l)}>
                      {l} page
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <FeedbackDialog
        isOpen={isFeedbackDialogOpen}
        onClose={() => setIsFeedbackDialogOpen(false)}
        submissionId={gradingSubmissionId || ""}
        onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
}

// ----- Page -----
export default function SubmissionsPage({
  assignmentId,
}: {
  assignmentId?: string;
}) {
  const { user } = useAuth();

  // Shared filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "submitted" | "reviewed" | "completed" | "returned"
  >("all");

  // Instructor server filters
  const [projectId, setProjectId] = useState<string>("all");
  const [courseId, setCourseId] = useState<string>("all");
  const [fromDate, setFromDate] = useState<string>(""); // YYYY-MM-DD
  const [toDate, setToDate] = useState<string>("");

  // Sort
  const [sortBy, setSortBy] = useState<string>("submitted_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Pagination
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);

  // Data
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0); // Add trigger for refetch

  // Filter options from API
  const [filterProjects, setFilterProjects] = useState<
    { id: string; title: string }[]
  >([]);
  const [filterCourses, setFilterCourses] = useState<
    { id: string; title: string }[]
  >([]);

  useEffect(() => {
    setPage(1);
  }, [
    searchTerm,
    statusFilter,
    projectId,
    courseId,
    fromDate,
    toDate,
    sortBy,
    sortOrder,
  ]);

  // Auto-poll every 10s while any submission is ai_grading
  const hasAnyAiGrading = submissions.some((s) => s.status === "ai_grading");
  useEffect(() => {
    if (!hasAnyAiGrading) return;
    const intervalId = setInterval(() => {
      setRefetchTrigger((n) => n + 1);
    }, 10000);
    return () => clearInterval(intervalId);
  }, [hasAnyAiGrading]);

  // Fetch list
  useEffect(() => {
    let alive = true;
    const fetchAll = async () => {
      try {
        setLoadingList(true);
        const params: any = {
          page,
          limit,
          sortBy,
          sortOrder,
        };
        if (statusFilter !== "all") params.status = statusFilter;
        if (projectId !== "all") params.projectId = projectId;
        if (courseId !== "all") params.courseId = courseId;
        if (fromDate) params.fromDate = fromDate;
        if (toDate) params.toDate = toDate;
        if (searchTerm.trim()) params.searchTerm = searchTerm.trim();

        const res = await getAllSubmissions(params);
        const data = (res as GetAllSubmissionsResponse).data as any;

        const arr: SubmissionEntity[] = Array.isArray(data?.checkpointProgress)
          ? data.checkpointProgress
          : [];
        const mapped = arr.map(mapApiItemToSubmission);

        if (!alive) return;
        setSubmissions(mapped);

        // filters
        const fd = data?.filterDataSource;
        setFilterProjects(
          Array.isArray(fd?.projects)
            ? fd.projects
                .filter((p: any) => p?.id && p?.title)
                .map((p: any) => ({ id: p.id, title: p.title }))
            : []
        );
        setFilterCourses(
          Array.isArray(fd?.courses)
            ? fd.courses
                .filter((c: any) => c?.id && (c?.name || c?.title))
                .map((c: any) => ({ id: c.id, title: c.name || c.title }))
            : []
        );

        // pagination
        const pg = data?.pagination;
        if (pg) {
          setTotalItems(pg.totalItems ?? 0);
          setTotalPages(pg.totalPages ?? 1);
        } else {
          setTotalItems(mapped.length);
          setTotalPages(1);
        }
      } catch (e: any) {
        toast.error(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to load submissions"
        );
      } finally {
        if (alive) setLoadingList(false);
      }
    };
    fetchAll();
    return () => {
      alive = false;
    };
  }, [
    page,
    limit,
    sortBy,
    sortOrder,
    statusFilter,
    projectId,
    courseId,
    fromDate,
    toDate,
    searchTerm,
    refetchTrigger, // Add refetchTrigger as dependency
  ]);

  const fetchSubmissionDetail = async (submissionId: string) => {
    try {
      setLoadingDetail(true);
      const res = await getSubmissionById(submissionId);
      const detailItem = (res as any)?.data ?? res;
      if (!detailItem) {
        toast.info("No details found for submission");
        return;
      }
      const detailed = mapApiItemToSubmission(detailItem as SubmissionEntity);
      setSubmissions((prev) =>
        prev.map((s) => (s.id === detailed.id ? { ...s, ...detailed } : s))
      );
    } catch (e: any) {
      toast.error(e?.message || "Failed to load submission details");
    } finally {
      setLoadingDetail(false);
    }
  };

  const onGradeSubmit = async (payload: GradeSubmissionRequest) => {
    const resp = await saveRubrics(payload);
    toast.success(resp.message || "Feedback submitted successfully");
  };

  const isStudent = user?.role === ROLES.STUDENT;
  const handleExport = async () => {
    try {
      const params: any = {
        page,
        limit,
        sortBy,
        sortOrder,
        isExport: true,
      };
      if (statusFilter !== "all") params.status = statusFilter;
      if (projectId !== "all") params.projectId = projectId;
      if (courseId !== "all") params.courseId = courseId;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (searchTerm.trim()) params.searchTerm = searchTerm.trim();

      const res = await getAllSubmissions(params);
      await downloadAsXlsx(
        res.data,
        `submission_export_${new Date().toISOString().split("T")[0]}.xlsx`
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to Export Submission Data");
    }
  };
  return (
    <div className="px-6 py-2 space-y-6 mt-4">
      <PageHeader
        title={isStudent ? "My Submissions" : "Submissions Review"}
        subtitle={
          isStudent
            ? "Track your assignment submissions and grades"
            : "Manage and grade student submission."
        }
      >
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4 mr-2" /> Export
        </Button>
      </PageHeader>

      {isStudent ? (
        <StudentSubmissionsView
          submissions={submissions}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={(v) => setStatusFilter(v as any)}
          courseFilter={"all"}
          setCourseFilter={() => {}}
          selectedSubmission={null}
          setSelectedSubmission={() => {}}
          onFetchDetail={fetchSubmissionDetail}
          isFetchingDetail={loadingDetail}
        />
      ) : (
        <InstructorSubmissionsView
          serverRows={submissions}
          loading={loadingList}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={(v) => setStatusFilter(v as any)}
          projectId={projectId}
          setProjectId={setProjectId}
          courseId={courseId}
          setCourseId={setCourseId}
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          page={page}
          setPage={(n) => setPage(Math.max(1, n))}
          limit={limit}
          setLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          totalPages={totalPages}
          totalItems={totalItems}
          filterProjects={filterProjects}
          filterCourses={filterCourses}
          onGradeSubmit={onGradeSubmit}
          onRefetchTrigger={() => setRefetchTrigger((prev) => prev + 1)}
        />
      )}
    </div>
  );
}
